import type { PhotoKind } from '@nargus/shared';
import { readTakenAt } from './exif';

export const IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
];

export const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

export const ACCEPTED_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES].join(',');

const THUMB_MAX_EDGE = 512;
const THUMB_TYPE = 'image/jpeg';
const THUMB_QUALITY = 0.8;
const VIDEO_TIMEOUT_MS = 10_000;

export interface MediaMeta {
  kind: PhotoKind;
  width?: number;
  height?: number;
  durationMs?: number;
  takenAt: Date;
  thumbnail?: Blob;
}

export function kindOf(contentType: string): PhotoKind | undefined {
  if (IMAGE_TYPES.includes(contentType)) return 'image';
  if (VIDEO_TYPES.includes(contentType)) return 'video';
  return undefined;
}

export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatDimensions(width?: number, height?: number): string | undefined {
  return width && height ? `${width} × ${height}` : undefined;
}

export async function readMedia(file: File): Promise<MediaMeta> {
  const kind = kindOf(file.type);
  const fallback = new Date(file.lastModified);

  if (kind === 'video') return readVideo(file, fallback);
  return readImage(file, fallback);
}

async function readImage(file: File, fallback: Date): Promise<MediaMeta> {
  const takenAt = (await readTakenAt(file)) ?? fallback;

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const thumbnail = await drawThumbnail(bitmap, bitmap.width, bitmap.height);
    const meta = {
      kind: 'image' as const,
      width: bitmap.width,
      height: bitmap.height,
      takenAt,
      thumbnail,
    };
    bitmap.close();
    return meta;
  } catch {
    return { kind: 'image', takenAt };
  }
}

async function readVideo(file: File, fallback: Date): Promise<MediaMeta> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'metadata';
  video.src = url;

  try {
    await settled(video, 'loadedmetadata');
    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    video.currentTime = duration > 0.2 ? 0.1 : 0;
    await settled(video, 'seeked');

    return {
      kind: 'video',
      width: video.videoWidth || undefined,
      height: video.videoHeight || undefined,
      durationMs: Math.round(duration * 1000),
      takenAt: fallback,
      thumbnail: await drawThumbnail(video, video.videoWidth, video.videoHeight),
    };
  } catch {
    return { kind: 'video', takenAt: fallback };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

function settled(video: HTMLVideoElement, event: 'loadedmetadata' | 'seeked') {
  return new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for ${event}`));
    }, VIDEO_TIMEOUT_MS);

    function cleanup() {
      window.clearTimeout(timer);
      video.removeEventListener(event, onDone);
      video.removeEventListener('error', onError);
    }

    function onDone() {
      cleanup();
      resolve();
    }

    function onError() {
      cleanup();
      reject(new Error('Video could not be read'));
    }

    video.addEventListener(event, onDone, { once: true });
    video.addEventListener('error', onError, { once: true });
  });
}

async function drawThumbnail(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<Blob | undefined> {
  if (!width || !height) return undefined;

  const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (!context) return undefined;
  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  return new Promise<Blob | undefined>((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? undefined), THUMB_TYPE, THUMB_QUALITY);
  });
}
